import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from 'react-dom';
import { FaTimes, FaDownload, FaEdit, FaExpand, FaPlay, FaPause, FaShare, FaVideo, FaInfoCircle } from "react-icons/fa";
import Carousel, { Modal, ModalGateway } from "react-images";
import ReactPlayer from 'react-player';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const GeneratedContent = ({
  contentType, // "image" | "video"
  isGeneratingContent,
  contentGenerated,
  generatedImgContent,
  generatedVidContent,
  artFormat,
  timeRemaining,
  selectedImages,
  isEditingContent,
  editedContent,
  setEditedContent,
  isPlaying,
  setIsPlaying,
  isPublishing,
  publishStatus,
  contentTimestamp,
  viewerIsOpen,
  currentLightboxImage,
  creditInfo,
  user,
  handleStopGeneration,
  handleToggleEdit,
  handlePublish,
  handleScheduleLater,
  openLightbox,
  closeLightbox,
  handleClearContent,
  // New props for session-wide carousel
  generatedImgHistory = [],
  sessionSelectedImageIndex,
  setSessionSelectedImageIndex,
  // Product data props for video creation
  textboxUploadedImages = [],
  productType,
  productItem,
  selectedDresses = [],
  selectedHumanModel,
  selectedEvent,
  selectedStyle,
  realLooks,
  // Creation option prop
  selectedCreationOption,
  // Aspect ratio for placeholder
  aspectRatio = "Portrait"
}) => {
  const [containerHeight, setContainerHeight] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const playerRef = useRef(null);
  const navigate = useNavigate();

  const isVideoMode = contentType === "video";

  // Calculate dynamic height based on viewport
  // Editor sidebar is positioned side-by-side on desktop, not vertically stacked
  useEffect(() => {
    const calculateHeight = () => {
      const viewportHeight = window.innerHeight;
      const headerHeight = 64; // Header height
      const verticalPadding = 32; // Top and bottom padding
      const bottomMargin = 24; // Small margin for breathing room
      
      const availableHeight = viewportHeight - headerHeight - verticalPadding - bottomMargin;
      setContainerHeight(Math.max(500, availableHeight)); // Increased minimum to 500px for better content display
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  // Flatten history into a single array of items with content linkage
  // Show all content types regardless of current mode
  const flattenedItems = Array.isArray(generatedImgHistory)
    ? generatedImgHistory.flatMap((entry) => {
        const images = entry.images || [];
        // No filtering - show all content types
        return images.map((img) => ({
          ...img,
          __entryContent: entry.content || '',
          __entryTimestamp: entry.timestamp || ''
        }));
      })
    : [];

  // Fallback for older state (single batch) if no history provided
  const currentContent = isVideoMode ? generatedVidContent : generatedImgContent;
  const singleBatch = flattenedItems.length === 0 && currentContent && Array.isArray(currentContent.images)
    ? currentContent.images
        // No filtering - show all content types
        .map((img) => ({ 
          ...img, 
          __entryContent: currentContent.content || '', 
          __entryTimestamp: contentTimestamp || '' 
        }))
    : [];

  // Calculate aspect ratio dimensions for placeholder
  const getAspectRatioDimensions = () => {
    // Common aspect ratios
    const aspectRatios = {
      'Portrait': { width: 9, height: 16 }, // 9:16
      'Landscape': { width: 16, height: 9 }, // 16:9
      'Square': { width: 1, height: 1 }, // 1:1
      'Story': { width: 9, height: 16 }, // 9:16 (same as portrait)
      'Post': { width: 4, height: 5 }, // 4:5
    };
    
    const ratio = aspectRatios[aspectRatio] || aspectRatios['Portrait'];
    return ratio;
  };

  const aspectRatioDims = getAspectRatioDimensions();

  // Add placeholder item when generating content
  const placeholderItem = isGeneratingContent ? {
    url: null,
    prompt: 'Generating...',
    isVideo: artFormat === "video",
    __isPlaceholder: true,
    __entryContent: '',
    __entryTimestamp: ''
  } : null;

  // Combine items with placeholder at the end
  const allItems = flattenedItems.length > 0 ? flattenedItems : singleBatch;
  const itemsWithPlaceholder = placeholderItem ? [...allItems, placeholderItem] : allItems;
  const totalItems = itemsWithPlaceholder.length;

  // Adjust selected index to account for placeholder
  // When only placeholder exists, select it (index 0)
  // When placeholder exists with other items, default to placeholder (last item)
  const defaultIndex = totalItems > 0 
    ? (placeholderItem && allItems.length > 0 ? totalItems - 1 : (placeholderItem ? 0 : totalItems - 1))
    : 0;
  
  const selectedIndex = typeof sessionSelectedImageIndex === 'number' && sessionSelectedImageIndex >= 0 && sessionSelectedImageIndex < totalItems
    ? sessionSelectedImageIndex
    : defaultIndex;

  const selectedItem = totalItems > 0 ? itemsWithPlaceholder[selectedIndex] : null;
  const selectedContentText = selectedItem && !selectedItem.__isPlaceholder ? (selectedItem.__entryContent || '') : (currentContent?.content || '');

  const handlePrev = () => {
    if (totalItems === 0) return;
    const prevIdx = (selectedIndex - 1 + totalItems) % totalItems;
    setSessionSelectedImageIndex(prevIdx);
    if (isVideoMode) setIsVideoPlaying(false);
  };

  const handleNext = () => {
    if (totalItems === 0) return;
    const nextIdx = (selectedIndex + 1) % totalItems;
    setSessionSelectedImageIndex(nextIdx);
    if (isVideoMode) setIsVideoPlaying(false);
  };

  // Handle video play/pause toggle
  const handleVideoToggle = useCallback(() => {
    setIsVideoPlaying(prev => !prev);
  }, []);

  // Handle video ready event for optimization
  const handleVideoReady = useCallback(() => {
    // Video is ready to play
  }, []);

  // Handle video error
  const handleVideoError = useCallback((error) => {
    console.error('Video playback error:', error);
    setIsVideoPlaying(false);
  }, []);

  const handleDownload = async () => {
    if (!selectedItem || !selectedItem.url) return;
    
    // Helper to extract file extension from MIME type
    const getExtensionFromMimeType = (mimeType) => {
      const mimeMap = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/gif': 'gif',
        'image/webp': 'webp',
        'image/svg+xml': 'svg',
        'video/mp4': 'mp4',
        'video/webm': 'webm',
        'video/quicktime': 'mov',
        'video/x-msvideo': 'avi'
      };
      return mimeMap[mimeType] || null;
    };
    
    // Helper to extract extension from URL
    const getExtensionFromUrl = (url) => {
      const match = url.match(/\.([a-zA-Z0-9]+)(?:[?#]|$)/);
      return match ? match[1].toLowerCase() : null;
    };
    
    const contentLabel = selectedItem.isVideo ? 'video' : 'image';
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
    
    try {
      // Fetch the file to get its actual type
      const response = await fetch(selectedItem.url, { mode: 'cors' });
      const blob = await response.blob();
      
      // Determine file extension from MIME type or URL
      const ext = getExtensionFromMimeType(blob.type) 
        || getExtensionFromUrl(selectedItem.url) 
        || (selectedItem.isVideo ? 'mp4' : 'png');
      
      const filename = `generated-${contentLabel}-${Date.now()}.${ext}`;
      
      // On mobile, try Web Share API first
      if (isMobile && navigator.share) {
        try {
          const file = new File([blob], filename, { type: blob.type });
          const shareData = { files: [file] };
          
          if (navigator.canShare && navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return;
          }
        } catch (err) {
          if (err.name === 'AbortError') return; // User cancelled
          console.error('Share failed, falling back to download:', err);
        }
      }
      
      // Standard download
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      
    } catch (err) {
      // Fallback to direct link if CORS or fetch fails
      console.error('Download failed, using direct link:', err);
      const ext = getExtensionFromUrl(selectedItem.url) || (selectedItem.isVideo ? 'mp4' : 'png');
      const link = document.createElement('a');
      link.href = selectedItem.url;
      link.download = `generated-${contentLabel}-${Date.now()}.${ext}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleMaximize = (e) => {
    const event = e || { preventDefault: () => {} };
    if (!selectedItem?.url) return;
    
    if (isVideoMode) {
      openLightbox(selectedItem.url, 'video');
    } else {
      openLightbox(event, { photo: { src: selectedItem.url, index: selectedIndex }, index: selectedIndex });
    }
  };

  const handleShare = async () => {
    if (!selectedItem || !selectedItem.url) return;
    
    const contentLabel = selectedItem.isVideo ? 'video' : 'image';
    
    // Check if Web Share API is supported
    if (navigator.share) {
      try {
        // Fetch the item as a blob for native sharing
        const response = await fetch(selectedItem.url, { mode: 'cors' });
        const blob = await response.blob();
        const ext = selectedItem.isVideo ? 'mp4' : 'png';
        const file = new File([blob], `generated-${contentLabel}.${ext}`, { type: blob.type });
        
        const shareData = {
          title: `Generated ${contentLabel.charAt(0).toUpperCase() + contentLabel.slice(1)}`,
          text: selectedContentText ? selectedContentText.substring(0, 100) : `Check out this cool AI ${contentLabel} from Taco!`,
          files: [file]
        };

        // Check if files can be shared
        if (navigator.canShare && navigator.canShare(shareData)) {
          await navigator.share(shareData);
        } else {
          // Fallback to sharing just the URL if files aren't supported
          await navigator.share({
            title: `Generated ${contentLabel.charAt(0).toUpperCase() + contentLabel.slice(1)}`,
            text: selectedContentText ? selectedContentText.substring(0, 100) : `Check out this cool AI ${contentLabel} from Taco!`,
            url: selectedItem.url
          });
        }
      } catch (err) {
        // User cancelled or error occurred
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      console.log('Web Share API not supported');
    }
  };

  // Create Video handler: stash image URL and product data, navigate to CreateVideo page (Image mode only)
  const handleCreateVideo = () => {
    try {
      if (!selectedItem || !selectedItem.url) {
        toast.error('No image to use for video creation');
        return;
      }

      const mediaUrl = selectedItem.url;
      const filename = `video-reference-${Date.now()}.png`;
      
      // Store image URL and name in sessionStorage
      try {
        sessionStorage.setItem('pendingEditImageUrl', mediaUrl);
        sessionStorage.setItem('pendingEditImageName', filename);
      } catch (_) {}

      // Store product data in sessionStorage for video page
      try {
        const productData = {
          productType,
          productItem,
          selectedDresses,
          selectedHumanModel,
          selectedEvent,
          selectedStyle,
          realLooks,
          // Include textbox images data if available
          textboxImages: textboxUploadedImages.map(img => ({
            dataUrl: img.dataUrl,
            name: img.name,
            type: img.type,
            size: img.size,
            productType: img.productType,
            productItem: img.productItem,
            productView: img.productView,
            productCoverage: img.productCoverage,
            productCloseupDetail: img.productCloseupDetail,
            ProductImgDtl: img.ProductImgDtl
          }))
        };
        sessionStorage.setItem('pendingVideoProductData', JSON.stringify(productData));
      } catch (err) {
        console.error('Error storing product data:', err);
      }

      navigate('/video');
    } catch (e) {
      console.error('Create video navigation error:', e);
      toast.error('Unable to open video creator');
    }
  };

  // Credit info display as overlay
  const getCreditText = () => {
    if (!creditInfo) return null;
    
    const { total_cost, time_taken_sec } = creditInfo;
    
    if (typeof total_cost === 'number' || typeof time_taken_sec === 'number') {
      const costText = typeof total_cost === 'number' ? `$${total_cost.toFixed(4)}` : null;
      const timeText = typeof time_taken_sec === 'number' ? `${time_taken_sec.toFixed(2)}s` : null;
      return [costText, timeText].filter(Boolean).join(' • ');
    }
    
    const tokensRemaining = creditInfo.credit_left_tokens || creditInfo.credit_left || creditInfo.tokens || null;
    if (tokensRemaining !== null && tokensRemaining !== undefined && tokensRemaining !== '') {
      return `💎 ${tokensRemaining} tokens remaining`;
    }
    
    return null;
  };

  return (
    <>
      {/* Generated Content Section - Show when content exists OR when generating */}
      {(contentGenerated || isGeneratingContent) && totalItems > 0 && (
        <div 
          className="w-full h-full flex items-center justify-center lg:items-stretch lg:justify-stretch"
          style={{ height: `${containerHeight}px` }}
        >
          <div className="relative w-full h-full flex flex-col lg:flex-row gap-4 max-w-7xl lg:max-w-none mx-auto lg:mx-0 px-4 lg:px-0">
            
            {/* Main Content Display */}
            <div className="flex-1 relative bg-black/20 rounded-2xl overflow-hidden backdrop-blur-sm border border-gray-600/30">
              
              {/* Action Buttons Overlay - Hide when showing placeholder */}
              {!selectedItem?.__isPlaceholder && (
                <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
                  <button
                    onClick={handleShare}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm lg:hidden"
                    title="Share"
                  >
                    <FaShare className="text-sm" />
                  </button>
                  {/* {!isVideoMode && (
                    <button
                      onClick={handleCreateVideo}
                      className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                      title="Create Video"
                    >
                      <FaVideo className="text-sm" />
                    </button>
                  )} */}
                  <button
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleMaximize(event);
                    }}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    title="Maximize"
                  >
                    <FaExpand className="text-sm" />
                  </button>
                  <button
                    onClick={handleDownload}
                    className="w-10 h-10 bg-black/60 hover:bg-black/80 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
                    title="Download"
                  >
                    <FaDownload className="text-sm" />
                  </button>
                  {/* {!isVideoMode && (
                    <button
                      onClick={selectedCreationOption === 'text_to_image' ? undefined : handleToggleEdit}
                      disabled={selectedCreationOption === 'text_to_image'}
                      className={`w-10 h-10 border border-gray-500/50 rounded-full flex items-center justify-center text-white transition-all duration-200 backdrop-blur-sm ${
                        selectedCreationOption === 'text_to_image' 
                          ? 'bg-gray-500/40 cursor-not-allowed opacity-50' 
                          : 'bg-black/60 hover:bg-black/80 hover:scale-110'
                      }`}
                      title={selectedCreationOption === 'text_to_image' ? 'Image edit is not possible for Text To Image option' : 'Edit'}
                    >
                      <FaEdit className="text-sm" />
                    </button>
                  )} */}
                </div>
              )}

              {/* Navigation Buttons */}
              {totalItems > 1 && !selectedItem?.__isPlaceholder && (
                <>
                  <button
                    onClick={handlePrev}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 backdrop-blur-sm border border-gray-500/50"
                    title={`Previous ${isVideoMode ? 'video' : 'image'}`}
                  >
                    ‹
                  </button>
                  <button
                    onClick={handleNext}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 backdrop-blur-sm border border-gray-500/50"
                    title={`Next ${isVideoMode ? 'video' : 'image'}`}
                  >
                    ›
                  </button>
                </>
              )}

              {/* Main Content (Image or Video) */}
              <div className="w-full h-full flex items-center justify-center p-4 relative">
                {selectedItem?.__isPlaceholder ? (
                  // Placeholder with wait message
                  <div 
                    className="w-full h-full flex items-center justify-center relative"
                    style={{
                      aspectRatio: `${aspectRatioDims.width} / ${aspectRatioDims.height}`,
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                  >
                    <div className="w-full h-full rounded-lg border-2 border-dashed border-white/20 bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center">
                      <div className="w-16 h-16 mb-6 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      </div>
                      
                      <h3 className="text-xl font-semibold text-white mb-3">Generating Content...</h3>
                      
                      <p className="text-gray-300 text-sm mb-4 leading-relaxed max-w-md">
                        Your content is being created. You will receive a notification once it is ready.
                      </p>
                      
                      <div className="flex items-center justify-center gap-2 text-xs text-gray-500 bg-white/5 rounded-lg py-2 px-4 mt-2">
                        <FaInfoCircle className="w-3 h-3" />
                        <span>You can check the Dashboard later if this page times out</span>
                      </div>
                    </div>
                  </div>
                ) : selectedItem?.url ? (
                  <>
                    {selectedItem.isVideo && isVideoMode ? (
                      <>
                        <ReactPlayer
                          ref={playerRef}
                          url={selectedItem.url}
                          width="100%"
                          height="100%"
                          playing={isVideoPlaying}
                          controls={false}
                          playsinline={true}
                          loop={true}
                          muted={false}
                          onReady={handleVideoReady}
                          onError={handleVideoError}
                          style={{ 
                            objectFit: 'contain',
                            borderRadius: '8px'
                          }}
                          config={{
                            file: {
                              attributes: {
                                preload: 'metadata'
                              }
                            }
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleMaximize(event);
                          }}
                        />
                        
                        {/* Custom Play/Pause Button Overlay */}
                        <div 
                          className="absolute inset-0 flex items-center justify-center cursor-pointer"
                          onClick={(event) => {
                            const rect = event.currentTarget.getBoundingClientRect();
                            const centerX = rect.left + rect.width / 2;
                            const centerY = rect.top + rect.height / 2;
                            const distance = Math.sqrt(
                              Math.pow(event.clientX - centerX, 2) + Math.pow(event.clientY - centerY, 2)
                            );
                            
                            if (distance < 50) {
                              handleVideoToggle();
                            } else {
                              handleMaximize(event);
                            }
                          }}
                        >
                          <div 
                            className="w-16 h-16 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 backdrop-blur-sm border border-gray-500/50"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleVideoToggle();
                            }}
                          >
                            {isVideoPlaying ? (
                              <FaPause className="text-white text-xl" />
                            ) : (
                              <FaPlay className="text-white text-xl ml-1" />
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <img
                        src={selectedItem.url}
                        alt={`Generated content`}
                        className="w-full h-full object-contain cursor-pointer rounded-lg"
                        onClick={(event) => openLightbox(event, { photo: { src: selectedItem.url, index: selectedIndex }, index: selectedIndex })}
                        style={{ maxHeight: '100%', maxWidth: '100%' }}
                      />
                    )}
                  </>
                ) : null}
              </div>

              {/* Thumbnail Strip Overlay - Bottom */}
              {totalItems > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {itemsWithPlaceholder.map((item, idx) => (
                    <button
                      key={item.__isPlaceholder ? `placeholder-${idx}` : `${item.url}-${idx}`}
                      className={`relative w-12 h-12 rounded-lg overflow-hidden border-2 transition-all duration-200 hover:scale-110 ${
                        idx === selectedIndex 
                          ? 'border-purple-400 ring-2 ring-purple-400/50' 
                          : 'border-gray-400/50 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        setSessionSelectedImageIndex(idx);
                        if (isVideoMode) setIsVideoPlaying(false);
                      }}
                      title={item.__isPlaceholder ? 'Generating...' : (item.prompt || `${isVideoMode ? 'Video' : 'Image'} ${idx + 1}`)}
                    >
                      {item.__isPlaceholder ? (
                        <div className="w-full h-full bg-gradient-to-br from-gray-900/80 to-gray-800/80 flex items-center justify-center border-2 border-dashed border-white/20">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        </div>
                      ) : item.isVideo ? (
                        <div className="w-full h-full bg-black">
                          <ReactPlayer 
                            url={item.url} 
                            width="100%" 
                            height="100%" 
                            playing={false} 
                            controls={false} 
                            playsinline={true} 
                            style={{ objectFit: 'cover' }} 
                          />
                        </div>
                      ) : (
                        <img 
                          src={item.url} 
                          alt={item.prompt || `thumb-${idx}`} 
                          className="w-full h-full object-cover" 
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Lightbox Modal - Different implementations for Image vs Video mode */}
      {isVideoMode ? (
        // Video mode: Custom portal-based lightbox
        viewerIsOpen && ReactDOM.createPortal(
          <div 
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-90 p-4"
            style={{ zIndex: 999999 }}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
              style={{ zIndex: 1000000 }}
            >
              <FaTimes className="text-3xl" />
            </button>
            <div
              className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {selectedItem?.isVideo ? (
                <ReactPlayer
                  url={selectedItem.url}
                  width="90vw"
                  height="90vh"
                  playing={true}
                  controls={true}
                  playsinline={true}
                  loop={true}
                  muted={false}
                  config={{
                    file: {
                      attributes: {
                        preload: 'auto',
                        controlsList: 'nodownload'
                      }
                    }
                  }}
                />
              ) : (
                <img
                  src={selectedItem?.url}
                  alt={selectedItem?.prompt || 'Generated content'}
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
              )}
            </div>
          </div>,
          document.body
        )
      ) : (
        // Image mode: react-images Modal/Carousel lightbox
        <ModalGateway>
          {viewerIsOpen ? (
            <Modal onClose={closeLightbox}>
              <Carousel
                currentIndex={currentLightboxImage}
                views={(allItems || []).map((image, index) => {
                  const imageUrl = image.url;
                  const isVideoFile = image.isVideo;
                  const imagePrompt = image.prompt || `Generated content ${index + 1}`;
                  
                  return {
                    src: imageUrl,
                    caption: imagePrompt,
                    isVideo: isVideoFile,
                    render: () => (
                      isVideoFile ? (
                        <div className="w-full h-full bg-black flex items-center justify-center">
                          <ReactPlayer
                            url={imageUrl}
                            width="100%"
                            height="100%"
                            playing={true}
                            controls={true}
                            playsinline={true}
                            loop={true}
                            muted={false}
                            style={{ objectFit: "contain" }}
                            config={{
                              file: {
                                attributes: {
                                  preload: 'auto',
                                  controlsList: 'nodownload'
                                }
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <img 
                          src={imageUrl} 
                          alt={imagePrompt}
                          className="max-w-full max-h-full object-contain"
                        />
                      )
                    )
                  };
                })}
              />
            </Modal>
          ) : null}
        </ModalGateway>
      )}
    </>
  );
};

export default GeneratedContent;

